import { memo } from 'react';
import PropTypes from 'prop-types';

import WidgetStatePlaceholder from '../components/WidgetStatePlaceholder';
import { useWidgetStateCopy } from '../context/WidgetStateCopyContext.jsx';
import { catalogWidgetEntry } from './catalogWidgetRegistry';
import { sampleFor } from './constants/catalogWidgetSamples';

/**
 * Draws a widget **body** — no card, no title, no chrome.
 *
 * The caller supplies the surface: `WidgetCardShell` on a dashboard, a
 * bordered panel in the builder's live preview, a clipped thumbnail in the
 * library. This is the one place that answers *"what does widget type X look
 * like"*, so those surfaces cannot drift apart.
 *
 * ## Data
 *
 * Pass `data` shaped for the type (see each widget's own propTypes). Omit it
 * and the widget draws its sample fixture, which is what the builder's live
 * preview and the library thumbnails want — they exist before any query has
 * run. Pass `data={null}` explicitly and you still get the sample; pass
 * `useSampleData={false}` to render the empty state instead, which is what a
 * real dashboard slot should do while it waits.
 *
 * ## Unknown types
 *
 * An id the registry does not know renders the empty state rather than a
 * generic block. A widget that shows *something* for a type nobody
 * implemented is worse than one that admits it has nothing: the first ships
 * to a customer, the second gets fixed.
 *
 * @param {object} props
 * @param {string} props.type Widget type id — `donut`, `stacked_bar`, `cost_kpi`.
 * @param {object} [props.data] Type-specific data. Omitted → the sample fixture.
 * @param {boolean} [props.useSampleData=true] Fall back to the sample when `data` is absent.
 * @param {boolean} [props.compact=false] Narrow slot: widgets that can shed detail do.
 * @param {string} [props.accent] Series colour override, for the types that plot one.
 * @param {string} [props.className]
 */
function CatalogWidget({
  type,
  data,
  useSampleData = true,
  compact = false,
  accent,
  className = '',
  ...rest
}) {
  const stateCopy = useWidgetStateCopy();
  const entry = catalogWidgetEntry(type);
  const resolved = data ?? (useSampleData ? sampleFor(type) : null);

  if (!entry || !resolved) {
    return <WidgetStatePlaceholder isEmpty stateCopy={stateCopy} />;
  }

  const { component: Component, props, compactAs } = entry;

  // Only the plotted types read `accent`, and only two components spell
  // "narrower" as `isCompressed`. Passing either unconditionally would hand
  // every widget props it never declared, which is how a component's propTypes
  // stop describing what it is actually called with.
  const body = (
    <Component
      data={resolved}
      {...props}
      {...(accent ? { accent } : null)}
      {...(compactAs ? { [compactAs]: compact } : null)}
      {...rest}
    />
  );

  // The wrapper only appears when there is a class to put on it: three of the
  // registry's components take no `className`, and an unconditional div would
  // add a block box to layouts — a sparkline beside a number, a stat row
  // inside a flex slot — that are sized by their parent.
  return className ? <div className={className}>{body}</div> : body;
}

CatalogWidget.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.object,
  useSampleData: PropTypes.bool,
  compact: PropTypes.bool,
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(CatalogWidget);
